import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@mui/material';
import { Stack } from '@mui/system';

import ModalNotification, {
  ModalNotificationProps,
} from '@/shared/components/dialogs/ModalNotification';
import BookingsService from '@/shared/types/bookings/service.type';

import BookingContactForm from '../../types/bookingContactForm.type';
import BookingServiceDetails from './components/BookingServiceDetails';
import ContactsDetails from './components/ContactsDetails';

enum Step {
  CreateBookingService = 'createBookingService',
  AddContacts = 'addContacts',
}

type StepState = {
  isActive: boolean;
  title: string;
  component: ReactNode;
  actions: ModalNotificationProps['dialogProps']['actions'];
};

type FormFields = {
  [key in keyof Pick<
    BookingsService,
    'booking_service_key' | 'booking_service_name' | 'booking_type'
  >]: {
    value: string;
    error: string;
  };
};

type CreateBookingServiceDialogProps = {
  loading: boolean;
  errors?: string[];
  onClose: () => void;
  proceed: (values: {
    bookingServiceDetails: Partial<BookingsService>;
    bookingContactsDetails: BookingContactForm[];
  }) => void;
};

const CreateBookingServiceDialog: FC<CreateBookingServiceDialogProps> = ({
  loading,
  errors,
  onClose,
  proceed,
}) => {
  const { t } = useTranslation(['bookingsServices', 'forms', 'common']);

  const proceedRef = useRef(proceed);

  const [activeStep, setActiveStep] = useState<Step>(Step.CreateBookingService);
  const [bookingServiceDetails, setBookingServiceDetails] =
    useState<FormFields>({
      booking_service_key: {
        value: '',
        error: '',
      },
      booking_service_name: {
        value: '',
        error: '',
      },
      booking_type: {
        value: '',
        error: '',
      },
    });
  const [bookingContactsDetails, setBookingContactsDetails] = useState<
    BookingContactForm[]
  >([]);
  const [triggerAddContactsValidation, setTriggerAddContactsValidation] =
    useState(false);

  useEffect(() => {
    if (bookingContactsDetails.length > 0) {
      const { booking_service_key, booking_service_name, booking_type } =
        bookingServiceDetails;

      proceedRef.current({
        bookingServiceDetails: {
          booking_service_key: booking_service_key.value,
          booking_service_name: booking_service_name.value,
          booking_type: booking_type.value,
        },
        bookingContactsDetails,
      });
    }
  }, [bookingServiceDetails, bookingContactsDetails]);

  const onValidateBookingServiceDetails = () => {
    const errors: {
      booking_service_key?: { error: string };
      booking_service_name?: { error: string };
      booking_type?: { error: string };
    } = {};

    if (!bookingServiceDetails.booking_service_key.value) {
      errors.booking_service_key = {
        error: t('forms:booking_service_key_required'),
      };
    } else {
      errors.booking_service_key = {
        error: '',
      };
    }

    if (!bookingServiceDetails.booking_service_name.value) {
      errors.booking_service_name = {
        error: t('forms:booking_service_name_required'),
      };
    } else {
      errors.booking_service_name = {
        error: '',
      };
    }

    if (!bookingServiceDetails.booking_type.value) {
      errors.booking_type = {
        error: t('forms:booking_type_required'),
      };
    } else {
      errors.booking_type = {
        error: '',
      };
    }

    setBookingServiceDetails({
      ...bookingServiceDetails,
      booking_service_key: {
        ...bookingServiceDetails.booking_service_key,
        ...errors.booking_service_key,
      },
      booking_service_name: {
        ...bookingServiceDetails.booking_service_name,
        ...errors.booking_service_name,
      },
      booking_type: {
        ...bookingServiceDetails.booking_type,
        ...errors.booking_type,
      },
    });

    return !Object.values(errors).some((item) => item.error !== '');
  };

  const activeFormConfig: { [key in Step]: StepState } = {
    [Step.CreateBookingService]: {
      isActive: true,
      title: t('forms:createBookingService'),
      component: (
        <BookingServiceDetails
          bookingServiceDetails={bookingServiceDetails}
          setBookingServiceDetails={(values) => {
            setBookingServiceDetails((prev) => ({ ...prev, ...values }));
          }}
        />
      ),
      actions: [
        {
          label: t('common:cancel'),
          onClick: onClose,
        },
        {
          label: t('common:next'),
          onClick: () => {
            const isValid = onValidateBookingServiceDetails();

            if (isValid) {
              setActiveStep(Step.AddContacts);
            }
          },
        },
      ],
    },
    [Step.AddContacts]: {
      isActive: false,
      title: t('forms:addContacts'),
      component: (
        <ContactsDetails
          shouldValidate={triggerAddContactsValidation}
          setShouldValidate={setTriggerAddContactsValidation}
          loading={loading}
          proceed={(values) => {
            setBookingContactsDetails(values);
          }}
        />
      ),
      actions: [
        {
          label: t('common:back'),
          onClick: () => setActiveStep(Step.CreateBookingService),
        },
        {
          label: t('common:finish'),
          onClick: () => {
            setTriggerAddContactsValidation((prev) => !prev);
          },
        },
      ],
    },
  };

  if (errors && errors.length > 0) {
    return (
      <ModalNotification
        size='xs'
        fullWidth
        open
        onClose={onClose}
        dialogProps={{
          title: t('forms:createBookingService'),
          content: (
            <Stack spacing={2} useFlexGap>
              {t('forms:bookingServiceCreateError')}
              {errors.map((error) => (
                <Alert key={error} severity='error'>
                  {error}
                </Alert>
              ))}
            </Stack>
          ),
          actions: [
            {
              label: t('common:close'),
              onClick: onClose,
            },
          ],
        }}
      />
    );
  }

  return (
    <ModalNotification
      size='sm'
      fullWidth
      open
      onClose={onClose}
      dialogProps={{
        title: activeFormConfig[activeStep].title,
        content: activeFormConfig[activeStep].component,
        actions: activeFormConfig[activeStep].actions || [],
      }}
    />
  );
};

export default CreateBookingServiceDialog;
export type { FormFields };
